import { Add, Assignment, Close, Info, PostAdd, Save, ManageAccounts } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Radio,
  RadioGroup,
  Switch,
  TextField,
  Theme,
  Typography
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { deepEqual } from 'fast-equals';
import { FieldArray, Form, Formik, getIn } from 'formik';
import { useSnackbar } from 'notistack';
import { FC, useEffect, useState, Fragment } from 'react';
import { Prompt, useHistory, useParams } from 'react-router-dom';
import * as Yup from 'yup';
// Components
import {
  Card,
  Loader,
  Page,
  PageTitleWithBack,
  SMBSelect,
  StatesDropdown
} from '../../../../components';
import {
  createVendor,
  getVendor,
  getVendorEntityTypes,
  getVendorTypes,
  updateVendor,
  uploadVendorCertificate,
  getAdminVendorContacts,
  getAdminSubcontractorsTypes
} from '../../../../fetch';
import {
  formatInputPhoneNumber,
  isSubcontractor,
  phoneRegExp,
  decimalLengthFourRegExp
} from '../../../../helpers';
import {
  ISubcontractorType,
  IVendorContact,
  IVendorDetail,
  IVendorEntityType,
  IVendorType
} from '../../../../models';
import { UploadFiles } from './UploadFiles';
import { VendorContacts } from './VendorContacts';
import { VendorInitContract } from './VendorInitContract';
import { VendorUsers } from './VendorUsers';

interface IManageVendorDetail {}

export const ManageVendorDetail: FC<IManageVendorDetail> = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [isSameChecked, setSameChecked] = useState(false);
  const [expirationDate, setExpirationDate] = useState<string | null>(null);
  const [isCertificateWarningChecked, setCertificateWarningChecked] = useState(false);
  const [isCertificateStopPayChecked, setCertificateStopPayChecked] = useState(false);
  const [selectedDocType, setSelectedDocType] = useState<string>('');
  const [isLoadingVendorEntityTypes, setLoadingVendorEntityTypes] = useState<boolean>(false);
  const [vendorEntityTypes, setVendorEntityTypes] = useState<IVendorEntityType[]>([]);
  const [actualWithholdForWorkersCompPercentage, setActualWithholdForWorkersCompPercentage] =
    useState<number>(0);
  const [
    actualWithholdForGeneralLiabilityPercentage,
    setActualWithholdForGeneralLiabilityPercentage
  ] = useState<number>(0);
  const [isLoadingVendor, setLoadingVendor] = useState<boolean>(false);
  const [currentVendor, setCurrentVendor] = useState<IVendorDetail | null>(null);
  const [isLoadingSubcontractorTypes, setLoadingSubcontractorTypes] = useState<boolean>(false);
  const [isLoadingVendorTypes, setLoadingVendorTypes] = useState<boolean>(false);
  const [vendorTypes, setVendorTypes] = useState<IVendorType[]>([]);
  const [allSubcontractorTypes, setAllSubcontractorTypes] = useState<ISubcontractorType[]>([]);
  const { vendorPartnerId } = useParams<{ vendorPartnerId: string }>();
  const [vendorContacts, setVendorContacts] = useState<IVendorContact[] | null>(null);
  const [isLoadingVendorContacts, setIsLoadingVendorContacts] = useState(true);
  const [inputSelectedSubcontractOptions, setInputSelectedSubcontractorOptions] = useState('');

  const VendorDetailSchema = Yup.object().shape({
    name: Yup.string().max(255, 'Max 255 characters').required('Required'),
    subcontractorTypeId: Yup.mixed().when('partnerType', {
      is: (val: string) => isSubcontractor(null, val),
      then: Yup.string().required('Required'),
      otherwise: Yup.string().notRequired()
    }),
    additionalSubcontractorTypes: Yup.array()
      .of(Yup.number())
      .test(
        'unique-values',
        'Subcontractor Types must be unique.',
        function (additionalTypes: number[]) {
          const primary = parseInt(this.parent.subcontractorTypeId);
          if (primary === null || additionalTypes === null) return true;
          return !additionalTypes?.includes(primary);
        }
      ),
    sageId: Yup.string().nullable(),
    isActive: Yup.boolean(),
    phoneNumber: Yup.string().matches(phoneRegExp, {
      excludeEmptyString: true,
      message: 'Invalid phone number'
    }),
    faxNumber: Yup.string().matches(phoneRegExp, {
      excludeEmptyString: true,
      message: 'Invalid phone number'
    }),
    isAllowedOnAnyPunchlist: Yup.boolean(),
    allowAnyEmailDomain: Yup.string(),
    physicalAddress: Yup.object({
      line1: Yup.string(),
      line2: Yup.string(),
      city: Yup.string(),
      state: Yup.string(),
      zip: Yup.string()
    }),
    billingAddress: Yup.object({
      line1: Yup.string(),
      line2: Yup.string(),
      city: Yup.string(),
      state: Yup.string(),
      zip: Yup.string()
    }),
    entityType: Yup.mixed().when('partnerType', {
      is: (val: string) => isSubcontractor(null, val),
      then: Yup.string().required('Required'),
      otherwise: Yup.string().notRequired()
    }),
    taxId: Yup.string(),
    taxIdIsCurrent: Yup.string(),
    crewAllotment: Yup.string(),
    hasWorkersComp: Yup.string(),
    workersCompExpiration: Yup.string().nullable(),
    withholdForWorkersComp: Yup.string(),
    withholdForWorkersCompPercentage: Yup.string(),
    hasGeneralLiability: Yup.string(),
    generalLiabilityExpiration: Yup.string().nullable(),
    withholdForGeneralLiability: Yup.string(),
    withholdForGeneralLiabilityPercentage: Yup.string(),
    additionallyInsured: Yup.string(),
    certificate: Yup.string(),
    partnerType: Yup.string(),
    vendorTypeId: Yup.string().required('Required'),
    emailDomains: Yup.mixed().when('restrictDomain', {
      is: (val: string) => val === 'Yes',
      then: Yup.array().of(
        Yup.string()
          .matches(
            /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9](?:\.[a-zA-Z]{2,})+$/,
            'Not a valid Domain name'
          )
          .required('Required')
      ),
      otherwise: Yup.array().notRequired()
    })
  });

  // api calls
  const fetchVendorContacts = async () => {
    if (currentVendor && currentVendor.vendorId) {
      try {
        setIsLoadingVendorContacts(true);
        const res = await getAdminVendorContacts(currentVendor.vendorId);
        setVendorContacts(res);
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoadingVendorContacts(false);
      }
    } else {
      setVendorContacts([]);
      setIsLoadingVendorContacts(false);
    }
  };

  const fetchAllVendorTypes = async () => {
    try {
      setLoadingVendorTypes(true);
      const res = await getVendorTypes();
      setVendorTypes(res.records);
    } catch (error) {
      console.error(error);
      enqueueSnackbar(`Error loading vendor types, please try again.`, {
        variant: 'error'
      });
    } finally {
      setLoadingVendorTypes(false);
    }
  };

  const fetchAllSubcontractorTypes = async () => {
    try {
      setLoadingSubcontractorTypes(true);
      const res = await getAdminSubcontractorsTypes();
      setAllSubcontractorTypes(res.records);
    } catch (error) {
      console.error(error);
      enqueueSnackbar(`Error loading subcontractor types, please try again.`, {
        variant: 'error'
      });
    } finally {
      setLoadingSubcontractorTypes(false);
    }
  };

  const fetchVendorDetail = async (id?: string | number) => {
    setLoadingVendor(true);
    try {
      const res = await getVendor(id || vendorPartnerId);
      setCurrentVendor(res);
    } catch (error) {
      enqueueSnackbar('Redirecting...', {
        variant: 'info'
      });
      enqueueSnackbar(error?.Detail ?? 'Error loading vendor, please try again', {
        variant: 'error'
      });
      // set minor timeout so the redirecting doesn't go too fast
      setTimeout(() => {
        history.push('/admin/vendor-tools/vendors');
      }, 1000);
    } finally {
      setLoadingVendor(false);
    }
  };

  //Need this context to make sure the users tab is set to external
  const history = useHistory();
  const classes = useStyles();

  const fetchVendorEntityTypes = async () => {
    try {
      setLoadingVendorEntityTypes(true);
      const res = await getVendorEntityTypes();
      setVendorEntityTypes(res);
    } catch (error) {
      console.error(error);
      enqueueSnackbar(`Error loading entity types, please try again.`, {
        variant: 'error'
      });
    } finally {
      setLoadingVendorEntityTypes(false);
    }
  };

  useEffect(() => {
    if (currentVendor && deepEqual(currentVendor.billingAddress, currentVendor.physicalAddress)) {
      setSameChecked(true);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentVendor]);

  useEffect(() => {
    if (currentVendor && currentVendor.vendorId) {
      fetchVendorContacts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentVendor]);

  useEffect(() => {
    fetchVendorEntityTypes();
    if (vendorPartnerId !== 'add') {
      fetchVendorDetail();
    }
    fetchAllSubcontractorTypes();
    fetchAllVendorTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorPartnerId]);

  return (
    <>
      <Formik
        enableReinitialize={true}
        initialValues={{
          name: currentVendor?.name ?? '',
          sageId: currentVendor?.sageId ?? '',
          subcontractorTypeId: currentVendor?.subcontractorTypeId ?? '',
          additionalSubcontractorTypes: currentVendor?.additionalSubcontractorTypes ?? [],
          isActive: currentVendor?.isActive === false ? currentVendor.isActive : true,
          phoneNumber: currentVendor?.phoneNumber ?? '',
          faxNumber: currentVendor?.faxNumber ?? '',
          restrictDomain: currentVendor?.allowAnyEmailDomain === false ? 'Yes' : 'No',
          isAllowedOnAnyPunchlist: currentVendor?.isAllowedOnAnyPunchlist ?? false,
          physicalAddress: {
            line1: currentVendor?.physicalAddress?.line1 ?? '',
            line2: currentVendor?.physicalAddress?.line2 ?? '',
            city: currentVendor?.physicalAddress?.city ?? '',
            state: currentVendor?.physicalAddress?.state ?? '',
            zip: currentVendor?.physicalAddress?.zip ?? ''
          },
          billingAddress: {
            line1: currentVendor?.billingAddress?.line1 ?? '',
            line2: currentVendor?.billingAddress?.line2 ?? '',
            city: currentVendor?.billingAddress?.city ?? '',
            state: currentVendor?.billingAddress?.state ?? '',
            zip: currentVendor?.billingAddress?.zip ?? ''
          },
          entityType: currentVendor?.entityType ?? '',
          taxId: currentVendor?.taxId ?? '',
          taxIdIsCurrent:
            currentVendor?.taxIdIsCurrent === false
              ? 'No'
              : currentVendor?.taxIdIsCurrent
              ? 'Yes'
              : '',
          crewAllotment: currentVendor?.crewAllotment ?? '',
          hasWorkersComp:
            currentVendor?.hasWorkersComp === false
              ? 'No'
              : currentVendor?.hasWorkersComp
              ? 'Yes'
              : '',
          workersCompExpiration: currentVendor?.workersCompExpiration
            ? currentVendor?.workersCompExpiration
            : '',
          withholdForWorkersComp:
            currentVendor?.withholdForWorkersComp === false
              ? 'No'
              : currentVendor?.withholdForWorkersComp
              ? 'Yes'
              : '',
          withholdForWorkersCompPercentage: currentVendor?.withholdForWorkersCompPercentage
            ? `${currentVendor.withholdForWorkersCompPercentage}%`
            : '',
          hasGeneralLiability:
            currentVendor?.hasGeneralLiability === false
              ? 'No'
              : currentVendor?.hasGeneralLiability
              ? 'Yes'
              : '',
          generalLiabilityExpiration: currentVendor?.generalLiabilityExpiration ?? '',
          withholdForGeneralLiability:
            currentVendor?.withholdForGeneralLiability === false
              ? 'No'
              : currentVendor?.withholdForGeneralLiability
              ? 'Yes'
              : '',
          withholdForGeneralLiabilityPercentage:
            currentVendor?.withholdForGeneralLiabilityPercentage
              ? `${currentVendor.withholdForGeneralLiabilityPercentage}%`
              : '',
          additionallyInsured:
            currentVendor?.additionallyInsured === false
              ? 'No'
              : currentVendor && currentVendor.additionallyInsured
              ? 'Yes'
              : '',
          certificate: '',
          vendorType: currentVendor?.vendorType ?? '',
          vendorTypeId: currentVendor?.vendorTypeId ?? '',
          emailDomains:
            currentVendor?.emailDomains.length !== 0 ? currentVendor?.emailDomains : ['']
        }}
        validationSchema={VendorDetailSchema}
        onSubmit={async (
          {
            entityType,
            taxId,
            crewAllotment,
            taxIdIsCurrent,
            hasWorkersComp,
            additionallyInsured,
            hasGeneralLiability,
            withholdForWorkersComp,
            withholdForWorkersCompPercentage,
            withholdForGeneralLiability,
            withholdForGeneralLiabilityPercentage,
            workersCompExpiration,
            generalLiabilityExpiration,
            ...rest
          },
          actions
        ) => {
          try {
            let res = null;
            const data = {
              ...rest,
              subcontractorTypeId: Number(rest.subcontractorTypeId) || null,
              vendorTypeId: Number(rest.vendorTypeId) || null,
              subcontractor: null,
              allowAnyEmailDomain: rest.restrictDomain === 'Yes' ? false : true,
              emailDomains:
                !rest.emailDomains || rest.restrictDomain === 'No' ? null : rest.emailDomains
            };

            if (isSubcontractor(null, currentVendor?.vendorType)) {
              data.subcontractor = {
                entityType,
                taxId: Number(taxId),
                crewAllotment: Number(crewAllotment),
                taxIdIsCurrent: taxIdIsCurrent === 'Yes' ? true : false,
                hasWorkersComp: hasWorkersComp === 'Yes' ? true : false,
                additionallyInsured: additionallyInsured === 'Yes' ? true : false,
                hasGeneralLiability: hasGeneralLiability === 'Yes' ? true : false,
                withholdForWorkersComp: withholdForWorkersComp === 'Yes' ? true : false,
                withholdForWorkersCompPercentage: Number(actualWithholdForWorkersCompPercentage),
                withholdForGeneralLiability: withholdForGeneralLiability === 'Yes' ? true : false,
                withholdForGeneralLiabilityPercentage: Number(
                  actualWithholdForGeneralLiabilityPercentage
                ),
                workersCompExpiration: workersCompExpiration || '',
                generalLiabilityExpiration: generalLiabilityExpiration || ''
              };
            }

            // not needed for post/put
            delete data.certificate;
            // only used for validation remove on post/put
            delete data.vendorType;

            if (currentVendor && currentVendor.vendorId) {
              res = await updateVendor(currentVendor.vendorId, data);
              if (pdfFile) {
                const formData = new FormData();
                formData.append('File', pdfFile);
                await uploadVendorCertificate(currentVendor.vendorId, formData, {
                  documentTypeId: Number(selectedDocType),
                  warning: isCertificateWarningChecked,
                  stopPay: isCertificateStopPayChecked,
                  expires: expirationDate
                });
              }
            } else {
              // remove from create
              delete data.sageId;

              res = await createVendor(data);
              if (res && typeof res === 'number' && pdfFile) {
                const formData = new FormData();
                formData.append('File', pdfFile);
                await uploadVendorCertificate(res, formData, {
                  documentTypeId: Number(selectedDocType),
                  warning: isCertificateWarningChecked,
                  stopPay: isCertificateStopPayChecked,
                  expires: expirationDate
                });
              }
            }

            await fetchVendorDetail(res && typeof res === 'number' ? res : undefined);
            if (res && typeof res === 'number') {
              history.push(`/admin/vendor-tools/vendors/${res}`);
            }
            enqueueSnackbar(currentVendor ? 'Vendor Updated!' : 'Vendor Created!', {
              variant: 'success'
            });
          } catch (error) {
            enqueueSnackbar(
              error?.Detail ??
                `Error ${currentVendor ? 'updating' : 'creating'} this vendor. Please try again.`,
              {
                variant: 'error'
              }
            );
          }
        }}
      >
        {({
          isSubmitting,
          values,
          initialValues,
          setFieldValue,
          errors,
          touched,
          handleSubmit,
          dirty,
          isValid,
          handleBlur,
          handleChange,
          setValues
        }) => {
          return (
            <Page
              title={isLoadingVendor ? 'Loading...' : currentVendor ? `Edit Vendor` : 'Add Vendor'}
            >
              <>
                <Prompt
                  when={!deepEqual(initialValues, values) || pdfFile ? true : false}
                  message="Are you sure you want to navigate away from this page? Your unsaved changes will be lost."
                />
                {(isSubmitting || isLoadingVendor) && (
                  <Loader type="fullscreen" position="centered" />
                )}
                {!isLoadingVendor && (
                  <Form onSubmit={handleSubmit} autoComplete="none">
                    <Box marginBottom="1rem" className={classes.title}>
                      <PageTitleWithBack
                        backText="Back to manage vendors"
                        backUrl="/admin/vendor-tools/vendors"
                        title={currentVendor ? `Edit Vendor` : 'Add Vendor'}
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            color="primary"
                            checked={values.isActive}
                            id="active-inactive"
                            onChange={() => setFieldValue('isActive', !values.isActive)}
                          />
                        }
                        label="Active"
                      />
                    </Box>
                    <Card icon={<Info />} title="Info">
                      <Grid container spacing={1} alignItems="top">
                        {currentVendor && currentVendor.sageId > 0 && (
                          <Grid item xs={12} sm={6} md={3}>
                            <TextField
                              fullWidth
                              variant="outlined"
                              autoComplete="nope"
                              label="Sage ID"
                              name="sageId"
                              value={values.sageId}
                              disabled
                              onBlur={handleBlur}
                              size="small"
                              onChange={e => setFieldValue('sageId', e.target.value)}
                            />
                          </Grid>
                        )}
                        <Grid item xs={12} sm={6} md={3}>
                          <TextField
                            fullWidth
                            variant="outlined"
                            autoComplete="nope"
                            label="Name"
                            name="name"
                            required
                            value={values.name}
                            onBlur={handleBlur}
                            size="small"
                            onChange={e => setFieldValue('name', e.target.value)}
                            error={Boolean(touched.name && errors.name)}
                            helperText={touched.name && errors.name}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <FormControl fullWidth required variant="outlined" size="small">
                            <SMBSelect
                              name="vendorTypeId"
                              label="Vendor Type"
                              id="vendorTypeId"
                              disabled={isLoadingVendorTypes}
                              required
                              variant="outlined"
                              handleClear={() => {
                                setFieldValue('vendorTypeId', '');
                                setFieldValue('vendorType', '');
                              }}
                              error={errors && errors.vendorTypeId ? true : false}
                              value={values.vendorTypeId}
                              onChange={e => {
                                setFieldValue('vendorTypeId', e.target.value as string);
                                setFieldValue(
                                  'vendorType',
                                  vendorTypes.filter(
                                    v => v.vendorTypeId === Number(e.target.value)
                                  )[0]?.name ?? ''
                                );
                              }}
                            >
                              {vendorTypes?.map((t: IVendorType, index) => {
                                return (
                                  <MenuItem key={`${index}`} value={t.vendorTypeId}>
                                    {t.name}
                                  </MenuItem>
                                );
                              })}
                            </SMBSelect>
                            {touched?.vendorTypeId && errors?.vendorTypeId && (
                              <FormHelperText error>Required</FormHelperText>
                            )}
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <FormControl
                            fullWidth
                            required={isSubcontractor(null, currentVendor?.vendorType)}
                            variant="outlined"
                            size="small"
                          >
                            <SMBSelect
                              fullWidth
                              variant="outlined"
                              name="subcontractorTypeId"
                              label="Primary Subcontractor Type"
                              id="subcontractorTypeId"
                              disabled={isLoadingSubcontractorTypes}
                              required={isSubcontractor(null, currentVendor?.vendorType)}
                              error={errors && errors.subcontractorTypeId ? true : false}
                              value={values.subcontractorTypeId}
                              handleClear={() => {
                                setFieldValue('subcontractorTypeId', '');
                              }}
                              onChange={e => {
                                setFieldValue('subcontractorTypeId', e.target.value as string);
                              }}
                            >
                              {allSubcontractorTypes?.map((t: ISubcontractorType, index) => {
                                return (
                                  <MenuItem key={`${index}`} value={t.subcontractorTypeId}>
                                    {t.name}
                                  </MenuItem>
                                );
                              })}
                            </SMBSelect>
                            {touched?.subcontractorTypeId && errors?.subcontractorTypeId && (
                              <FormHelperText error>Required</FormHelperText>
                            )}
                          </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                          <FormControl fullWidth variant="outlined" size="small">
                            <Autocomplete
                              multiple
                              id="additionalSubcontractorTypes"
                              value={allSubcontractorTypes.filter(types =>
                                values.additionalSubcontractorTypes.includes(
                                  types.subcontractorTypeId
                                )
                              )}
                              onChange={(event, newValue: ISubcontractorType[]) => {
                                setFieldValue(
                                  'additionalSubcontractorTypes',
                                  newValue.map(val => val.subcontractorTypeId)
                                );
                              }}
                              inputValue={inputSelectedSubcontractOptions}
                              onInputChange={(event, newInputValue) => {
                                setFieldValue('additionalSubcontractorTypes', newInputValue);
                                setInputSelectedSubcontractorOptions(newInputValue);
                              }}
                              options={allSubcontractorTypes.filter(
                                st => st.subcontractorTypeId !== values.subcontractorTypeId
                              )}
                              getOptionLabel={(option: ISubcontractorType) => option.name}
                              filterSelectedOptions
                              renderOption={(props, option: ISubcontractorType) => {
                                return (
                                  <li {...props} key={`${option.subcontractorTypeId}`}>
                                    {option.name}
                                  </li>
                                );
                              }}
                              renderInput={params => (
                                <TextField
                                  {...params}
                                  size="small"
                                  variant="outlined"
                                  disabled={isLoadingSubcontractorTypes}
                                  label={'Additional Subcontractor Types'}
                                  placeholder={
                                    isLoadingSubcontractorTypes
                                      ? 'Loading...'
                                      : 'Subcontractor Types'
                                  }
                                  error={!!errors?.additionalSubcontractorTypes?.length}
                                  helperText={errors?.additionalSubcontractorTypes}
                                />
                              )}
                            />
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <TextField
                            fullWidth
                            variant="outlined"
                            autoComplete="nope"
                            label="Phone Number"
                            name="phoneNumber"
                            value={values.phoneNumber}
                            onBlur={handleBlur}
                            size="small"
                            onChange={e =>
                              setFieldValue('phoneNumber', formatInputPhoneNumber(e.target.value))
                            }
                            error={Boolean(touched.phoneNumber && errors.phoneNumber)}
                            helperText={touched.phoneNumber && errors.phoneNumber}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <TextField
                            fullWidth
                            variant="outlined"
                            autoComplete="nope"
                            label="Fax Number"
                            name="faxNumber"
                            value={values.faxNumber}
                            onBlur={handleBlur}
                            size="small"
                            onChange={e =>
                              setFieldValue('faxNumber', formatInputPhoneNumber(e.target.value))
                            }
                            error={Boolean(touched.faxNumber && errors.faxNumber)}
                            helperText={touched.faxNumber && errors.faxNumber}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <FormControlLabel
                            color="primary"
                            control={
                              <Checkbox
                                checked={values.isAllowedOnAnyPunchlist}
                                id="allow-assignment-on-any-punchlist"
                                color="primary"
                                onBlur={handleBlur}
                                onClick={e => {
                                  setFieldValue(
                                    'isAllowedOnAnyPunchlist',
                                    !values.isAllowedOnAnyPunchlist
                                  );
                                }}
                              />
                            }
                            label="Allow assignment on any Punchlist"
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <Typography variant="h6">Physical Address</Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            variant="outlined"
                            autoComplete="nope"
                            label="Address Line 1"
                            name="line1"
                            value={values.physicalAddress.line1}
                            onBlur={handleBlur}
                            size="small"
                            onChange={e => setFieldValue('physicalAddress.line1', e.target.value)}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            variant="outlined"
                            autoComplete="nope"
                            label="Address Line 2"
                            name="line2"
                            value={values.physicalAddress.line2}
                            onBlur={handleBlur}
                            size="small"
                            onChange={e => setFieldValue('physicalAddress.line2', e.target.value)}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            variant="outlined"
                            autoComplete="nope"
                            label="City"
                            name="city"
                            value={values.physicalAddress.city}
                            onBlur={handleBlur}
                            size="small"
                            onChange={e => setFieldValue('physicalAddress.city', e.target.value)}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <StatesDropdown
                            value={values.physicalAddress.state}
                            handleChange={value => {
                              setFieldValue('physicalAddress.state', value as string);
                            }}
                            isAdminPortal={true}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            variant="outlined"
                            autoComplete="nope"
                            label="Postal Code"
                            name="postalCode"
                            value={values.physicalAddress.zip}
                            onBlur={handleBlur}
                            size="small"
                            onChange={e => {
                              // only numbers
                              const re = /^[0-9\b]+$/;
                              // if value is not blank, then test the regex
                              if (e.target.value === '' || re.test(e.target.value)) {
                                setFieldValue('physicalAddress.zip', e.target.value);
                              }
                            }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="h6">Billing Address</Typography>
                          <FormControlLabel
                            color="primary"
                            control={
                              <Checkbox
                                checked={isSameChecked}
                                id="orders-checkbox"
                                color="primary"
                                onClick={e => {
                                  e.stopPropagation();
                                  setSameChecked(!isSameChecked);
                                  if (isSameChecked) {
                                    setValues({
                                      ...values,
                                      billingAddress: {
                                        line1: '',
                                        line2: '',
                                        city: '',
                                        state: '',
                                        zip: ''
                                      }
                                    });
                                  } else {
                                    setValues({
                                      ...values,
                                      billingAddress: {
                                        ...values.physicalAddress
                                      }
                                    });
                                  }
                                }}
                              />
                            }
                            label="Same as Physical Address"
                          />
                        </Grid>
                        {!isSameChecked && (
                          <>
                            <Grid item xs={12} md={6}>
                              <TextField
                                fullWidth
                                variant="outlined"
                                autoComplete="nope"
                                label="Address Line 1"
                                name="line1"
                                value={values.billingAddress.line1}
                                onBlur={handleBlur}
                                size="small"
                                onChange={e =>
                                  setFieldValue('billingAddress.line1', e.target.value)
                                }
                              />
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <TextField
                                fullWidth
                                variant="outlined"
                                autoComplete="nope"
                                label="Address Line 2"
                                name="line2"
                                value={values.billingAddress.line2}
                                onBlur={handleBlur}
                                size="small"
                                onChange={e =>
                                  setFieldValue('billingAddress.line2', e.target.value)
                                }
                              />
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <TextField
                                fullWidth
                                variant="outlined"
                                autoComplete="nope"
                                label="City"
                                name="city"
                                value={values.billingAddress.city}
                                onBlur={handleBlur}
                                size="small"
                                onChange={e => setFieldValue('billingAddress.city', e.target.value)}
                              />
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <StatesDropdown
                                value={values.billingAddress.state}
                                handleChange={value => {
                                  setFieldValue('billingAddress.state', value as string);
                                }}
                                isAdminPortal={true}
                              />
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <TextField
                                fullWidth
                                variant="outlined"
                                autoComplete="nope"
                                label="Postal Code"
                                name="postalCode"
                                value={values.billingAddress.zip}
                                onBlur={handleBlur}
                                size="small"
                                onChange={e => {
                                  // only numbers
                                  const re = /^[0-9\b]+$/;
                                  // if value is not blank, then test the regex
                                  if (e.target.value === '' || re.test(e.target.value)) {
                                    setFieldValue('billingAddress.zip', e.target.value);
                                  }
                                }}
                              />
                            </Grid>
                          </>
                        )}
                      </Grid>
                    </Card>
                    {isSubcontractor(null, values.vendorType) && (
                      <Card icon={<Assignment />} title="Details">
                        <Grid container spacing={1} alignItems="center">
                          <Grid item xs={12} md={6}>
                            <FormControl
                              fullWidth
                              required={isSubcontractor(null, values.vendorType)}
                              variant="outlined"
                              size="small"
                            >
                              <SMBSelect
                                required={isSubcontractor(null, values.vendorType)}
                                name="entityType"
                                label="Entity Type"
                                variant="outlined"
                                disabled={isLoadingVendorEntityTypes}
                                id="entityType"
                                value={values.entityType}
                                error={errors && errors.entityType ? true : false}
                                onChange={e => {
                                  setFieldValue('entityType', e.target.value as string);
                                }}
                              >
                                {vendorEntityTypes &&
                                  vendorEntityTypes.map((entity: IVendorEntityType, index) => {
                                    return (
                                      <MenuItem key={`${index}`} value={entity.value}>
                                        {entity.shorthand}
                                      </MenuItem>
                                    );
                                  })}
                              </SMBSelect>
                              {touched && touched.entityType && errors && errors.entityType && (
                                <FormHelperText error>Required</FormHelperText>
                              )}
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              variant="outlined"
                              autoComplete="nope"
                              label="Crew Allotment"
                              name="crewAllotment"
                              value={values.crewAllotment}
                              onBlur={handleBlur}
                              size="small"
                              onChange={e => {
                                // only numbers
                                const re = /^[0-9\b]+$/;
                                // if value is not blank, then test the regex
                                if (e.target.value === '' || re.test(e.target.value)) {
                                  setFieldValue('crewAllotment', e.target.value);
                                }
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              variant="outlined"
                              autoComplete="nope"
                              label="Tax ID"
                              name="taxId"
                              value={values.taxId}
                              onBlur={handleBlur}
                              size="small"
                              onChange={e => {
                                // only numbers
                                const re = /^[0-9\b]+$/;
                                // if value is not blank, then test the regex
                                if (e.target.value === '' || re.test(e.target.value)) {
                                  setFieldValue('taxId', e.target.value);
                                }
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <FormControl fullWidth variant="outlined" size="small">
                              <Typography>Tax ID Is Current</Typography>
                              <RadioGroup
                                value={values.taxIdIsCurrent}
                                onChange={e => setFieldValue('taxIdIsCurrent', e.target.value)}
                                aria-label="hasInspectionDept"
                                name="hasInspectionDept"
                                className={classes.radioWrapper}
                              >
                                <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                                <FormControlLabel value="No" control={<Radio />} label="No" />
                              </RadioGroup>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <FormControl fullWidth variant="outlined" size="small">
                              <Typography>Workers Comp</Typography>
                              <RadioGroup
                                value={values.hasWorkersComp}
                                onChange={e => {
                                  setFieldValue('hasWorkersComp', e.target.value);
                                  if (e.target.value === 'Yes') {
                                    setFieldValue('withholdForWorkersComp', '');
                                    setFieldValue('withholdForWorkersCompPercentage', '');
                                  }
                                }}
                                aria-label="has Workers Comp"
                                name="hasWorkersComp"
                                className={classes.radioWrapper}
                              >
                                <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                                <FormControlLabel value="No" control={<Radio />} label="No" />
                              </RadioGroup>
                            </FormControl>
                          </Grid>
                          {values.hasWorkersComp === 'No' && (
                            <>
                              <Grid item xs={12} md={3}>
                                <FormControl fullWidth variant="outlined" size="small">
                                  <Typography>Workers Comp Withhold</Typography>
                                  <RadioGroup
                                    value={values.withholdForWorkersComp}
                                    onChange={e =>
                                      setFieldValue('withholdForWorkersComp', e.target.value)
                                    }
                                    aria-label="withhold For Workers Comp"
                                    name="withholdForWorkersComp"
                                    className={classes.radioWrapper}
                                  >
                                    <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                                    <FormControlLabel value="No" control={<Radio />} label="No" />
                                  </RadioGroup>
                                </FormControl>
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <TextField
                                  fullWidth
                                  variant="outlined"
                                  autoComplete="nope"
                                  label="Workers Comp Percentage"
                                  name="withholdForWorkersCompPercentage"
                                  value={values.withholdForWorkersCompPercentage}
                                  size="small"
                                  onBlur={e => {
                                    // Need to strip out all non numeric values from formatted value
                                    const value = e.target.value.replace('%', '').replace(',', '');
                                    // make sure it is number only before we update state
                                    if (value === '' || decimalLengthFourRegExp.test(value)) {
                                      setActualWithholdForWorkersCompPercentage(Number(value));
                                    }
                                    if (e.target.value) {
                                      setFieldValue(
                                        'withholdForWorkersCompPercentage',
                                        `${e.target.value}%`
                                      );
                                    }
                                  }}
                                  onChange={e => {
                                    // if value is not blank, then test the regex
                                    if (
                                      e.target.value === '' ||
                                      decimalLengthFourRegExp.test(e.target.value)
                                    ) {
                                      setFieldValue(
                                        'withholdForWorkersCompPercentage',
                                        e.target.value
                                      );
                                    }
                                  }}
                                />
                              </Grid>
                            </>
                          )}
                          <Grid item xs={12} md={3}>
                            <FormControl fullWidth variant="outlined" size="small">
                              <Typography>General Liability</Typography>
                              <RadioGroup
                                value={values.hasGeneralLiability}
                                onChange={e => {
                                  setFieldValue('hasGeneralLiability', e.target.value);
                                  if (e.target.value === 'Yes') {
                                    setFieldValue('withholdForGeneralLiability', '');
                                    setFieldValue('withholdForGeneralLiabilityPercentage', '');
                                  }
                                }}
                                aria-label="hasInspectionDept"
                                name="hasInspectionDept"
                                className={classes.radioWrapper}
                              >
                                <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                                <FormControlLabel value="No" control={<Radio />} label="No" />
                              </RadioGroup>
                            </FormControl>
                          </Grid>
                          {values.hasGeneralLiability === 'No' && (
                            <>
                              <Grid item xs={12} md={3}>
                                <FormControl fullWidth variant="outlined" size="small">
                                  <Typography>General Liability Withhold</Typography>
                                  <RadioGroup
                                    value={values.withholdForGeneralLiability}
                                    onChange={e =>
                                      setFieldValue('withholdForGeneralLiability', e.target.value)
                                    }
                                    aria-label="withhold For General Liability"
                                    name="withholdForGeneralLiability"
                                    className={classes.radioWrapper}
                                  >
                                    <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                                    <FormControlLabel value="No" control={<Radio />} label="No" />
                                  </RadioGroup>
                                </FormControl>
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <TextField
                                  fullWidth
                                  variant="outlined"
                                  autoComplete="nope"
                                  label="General Liability Percentage"
                                  name="withholdForGeneralLiabilityPercentage"
                                  value={values.withholdForGeneralLiabilityPercentage}
                                  size="small"
                                  onBlur={e => {
                                    // Need to strip out all non numeric values from formatted value
                                    const value = e.target.value.replace('%', '').replace(',', '');
                                    // make sure it is number only before we update state
                                    if (value === '' || decimalLengthFourRegExp.test(value)) {
                                      setActualWithholdForGeneralLiabilityPercentage(Number(value));
                                    }
                                    if (e.target.value) {
                                      setFieldValue(
                                        'withholdForGeneralLiabilityPercentage',
                                        `${e.target.value}%`
                                      );
                                    }
                                  }}
                                  onChange={e => {
                                    // if value is not blank, then test the regex
                                    if (
                                      e.target.value === '' ||
                                      decimalLengthFourRegExp.test(e.target.value)
                                    ) {
                                      setFieldValue(
                                        'withholdForGeneralLiabilityPercentage',
                                        e.target.value
                                      );
                                    }
                                  }}
                                />
                              </Grid>
                            </>
                          )}
                          <Grid item xs={12}>
                            <FormControl fullWidth variant="outlined" size="small">
                              <Typography>Are We Additionally Insured</Typography>
                              <RadioGroup
                                value={values.additionallyInsured}
                                onChange={e => setFieldValue('additionallyInsured', e.target.value)}
                                aria-label="hasInspectionDept"
                                name="hasInspectionDept"
                                className={classes.radioWrapper}
                              >
                                <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                                <FormControlLabel value="No" control={<Radio />} label="No" />
                              </RadioGroup>
                            </FormControl>
                          </Grid>
                        </Grid>
                      </Card>
                    )}
                    <Card icon={<PostAdd />} title="Documents and Files">
                      {currentVendor && (
                        <>
                          <VendorInitContract
                            currentVendor={currentVendor}
                            fetchCurrentVendor={fetchVendorDetail}
                            isSubcontractor={isSubcontractor(null, currentVendor?.vendorType)}
                            vendorContacts={vendorContacts}
                            isLoadingVendorContacts={isLoadingVendorContacts}
                          />
                        </>
                      )}
                      <Grid container spacing={1} alignItems="flex-start">
                        <UploadFiles
                          currentVendor={currentVendor}
                          pdfFile={pdfFile}
                          setPdfFile={setPdfFile}
                          setFieldValue={setFieldValue}
                          setCertificateStopPayChecked={setCertificateStopPayChecked}
                          setCertificateWarningChecked={setCertificateWarningChecked}
                          selectedDocType={selectedDocType}
                          isCertificateWarningChecked={isCertificateWarningChecked}
                          isCertificateStopPayChecked={isCertificateStopPayChecked}
                          setSelectedDocType={setSelectedDocType}
                          setExpirationDate={setExpirationDate}
                          expirationDate={expirationDate}
                        />
                      </Grid>
                    </Card>
                    <Card icon={<ManageAccounts />} title="Vendor User Management">
                      <Grid container item xs={12} alignItems={'flex-start'}>
                        <Grid item xs={12} md={6}>
                          <FormControl fullWidth variant="outlined" size="small" required>
                            <Typography>Restrict Domain</Typography>
                            <RadioGroup
                              sx={{
                                position: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'left',
                                alignItems: 'start'
                              }}
                              value={values.restrictDomain}
                              onChange={e => setFieldValue('restrictDomain', e.target.value)}
                              aria-label="restrictDomain"
                              name="restrictDomain"
                            >
                              <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                              <FormControlLabel value="No" control={<Radio />} label="No" />
                            </RadioGroup>
                          </FormControl>
                        </Grid>
                        {values.restrictDomain === 'Yes' && (
                          <Grid item xs={12} md={6} pt={0.5}>
                            <FieldArray name="emailDomains">
                              {({ push, remove }) => {
                                return (
                                  <>
                                    <Grid container spacing={1} alignItems="flex-start">
                                      {values?.emailDomains?.map((domain, index) => {
                                        const text = `emailDomains[${index}]`;
                                        const touchedText = getIn(touched, text);
                                        const errorText = getIn(errors, text);
                                        return (
                                          <Fragment key={`${index}`}>
                                            <Grid item xs={12}>
                                              <FormControl
                                                required
                                                fullWidth
                                                error={!domain}
                                                variant="outlined"
                                                size="small"
                                              >
                                                <TextField
                                                  fullWidth
                                                  required
                                                  variant="outlined"
                                                  autoComplete="nope"
                                                  label={`Domain ${index + 1}`}
                                                  name={`emailDomains.${index}`}
                                                  value={domain}
                                                  size="small"
                                                  onChange={handleChange}
                                                  onBlur={handleBlur}
                                                  helperText={
                                                    touchedText && errorText ? errorText : ''
                                                  }
                                                  error={Boolean(touchedText && errorText)}
                                                  InputProps={{
                                                    endAdornment:
                                                      values.emailDomains?.length > 1 ? (
                                                        <InputAdornment position="end">
                                                          <IconButton
                                                            aria-label="remove domain"
                                                            onClick={() => remove(index)}
                                                            edge="end"
                                                          >
                                                            <Close />
                                                          </IconButton>
                                                        </InputAdornment>
                                                      ) : null
                                                  }}
                                                />
                                              </FormControl>
                                            </Grid>
                                          </Fragment>
                                        );
                                      })}
                                    </Grid>
                                    <Grid
                                      marginTop={'1rem'}
                                      marginLeft={'auto'}
                                      textAlign={'right'}
                                      item
                                      xs={12}
                                      sm={3}
                                    >
                                      <Button
                                        id="add-vendor-domain"
                                        startIcon={<Add />}
                                        variant="contained"
                                        onClick={() => {
                                          push('');
                                        }}
                                      >
                                        Add
                                      </Button>
                                    </Grid>
                                  </>
                                );
                              }}
                            </FieldArray>
                          </Grid>
                        )}
                      </Grid>
                    </Card>
                    <VendorUsers
                      currentVendor={currentVendor}
                      vendorContacts={vendorContacts}
                      isLoadingVendorContacts={isLoadingVendorContacts}
                      fetchVendorContacts={fetchVendorContacts}
                    />
                    <VendorContacts currentVendor={currentVendor} />
                    <Box marginTop="2rem">
                      <Button
                        disabled={
                          !dirty ||
                          isSubmitting ||
                          !isValid ||
                          (values.certificate && !expirationDate && !selectedDocType ? true : false)
                        }
                        type="submit"
                        startIcon={<Save />}
                        variant="contained"
                        color="primary"
                      >
                        Save
                      </Button>
                      <Button
                        type="button"
                        variant="contained"
                        color="inherit"
                        startIcon={<Close />}
                        onClick={() => {
                          history.push('/admin/vendor-tools/vendors');
                        }}
                      >
                        Cancel
                      </Button>
                    </Box>
                  </Form>
                )}
              </>
            </Page>
          );
        }}
      </Formik>
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  radioWrapper: {
    flexDirection: 'row'
  },

  title: {
    display: 'flex',
    justifyContent: 'space-between'
  },
  removeDomainButtonWrapper: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '-8px'
  }
}));
